import { cloneDeep } from 'lodash'
import { Dictionary, UserProfile } from '@feedyou/schema'
import { isNotEmptyElement } from '@feedyou/utils'

import { AuthorId, Comment, CommentsPatch } from '../../types'
import { groupByUniqueIdProp } from '../../../../lib/array'
import { NormalizedCommentsWithDeps } from '../types'

export type DenormalizedComment = Omit<Comment, 'authorId' | 'resolved'> & {
  author: UserProfile
  resolved?: {
    by: UserProfile
    timestamp: number
  }
}

export const normalizeComment = ({ resolved, author, ...rest }: DenormalizedComment): Comment => {
  return {
    ...rest,
    authorId: author?.id,
    resolved: resolved ? { byAuthorId: resolved.by.id!, timestamp: resolved.timestamp } : undefined
  }
}

export const normalizeComments = (response: DenormalizedComment[]): NormalizedCommentsWithDeps => {
  const comments = response.map(normalizeComment)
  const authors = response
    .flatMap(comment => [comment.author, comment.resolved?.by])
    .filter(isNotEmptyElement)

  return {
    authorsById: groupByUniqueIdProp(authors, 'id'),
    commentsById: groupByUniqueIdProp(comments, 'id')
  }
}

export const denormalizePatch = (
  commentsPatch: CommentsPatch,
  authors: Dictionary<AuthorId, UserProfile>
): CommentsPatch => {
  const commentsPatchCopy: CommentsPatch = cloneDeep(commentsPatch)
  const firstCommentId = Object.keys(commentsPatchCopy)[0]

  // @ts-ignore
  commentsPatchCopy[firstCommentId] = denormalizeComment(
    commentsPatchCopy[firstCommentId] as Comment,
    authors
  )

  return commentsPatchCopy
}

export const denormalizeComment = (
  comment: Comment,
  authors: Dictionary<AuthorId, UserProfile>
): DenormalizedComment => {
  const { authorId, resolved, ...rest } = comment

  return {
    ...rest,
    author: authors[authorId!],
    resolved: resolved?.byAuthorId
      ? { by: authors[resolved.byAuthorId]!, timestamp: resolved.timestamp }
      : undefined
  } as DenormalizedComment
}
